import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography } from '@mui/material';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import { CardWrapper } from '../../UI';
import * as C from '../../../theme/colors.constants';

/**
 * Aba Fases — print `grid_check_fases.png`.
 *
 * Timeline vertical alternada: os cards vão para a direita ou para a esquerda da
 * linha central, e cada nó é um ícone de "caminhando" (o nó da fase atual é um
 * círculo vermelho, sem ícone). A alternância é feita por `flexDirection`
 * `row` / `row-reverse` a cada índice ímpar, e a linha central é um pseudo-
 * elemento do container.
 *
 * AW2-1107 Etapa 0: `fasesTimeline` (array já mapeado pelo container via
 * `fasesController.mapearFasesTimeline`) substitui o mock estático
 * `FASES_TIMELINE` — mesmo shape de campo (`nome`/`atual`/`entrada`/
 * `trabalhado`/`saida`/`negociador`), visual do componente inalterado.
 */
function CardFase({ fase }) {
  return (
    <Paper
      sx={{
        p: 1.75,
        border: `1px solid ${fase.atual ? C.tomato : C.borderGrey}`,
        maxWidth: 340,
      }}
    >
      <Typography
        sx={{
          fontWeight: 700,
          mb: 0.5,
          color: fase.atual ? C.tomato : C.black,
          fontSize: 'clamp(13px, 0.78vw, 18px)',
        }}
      >
        {fase.atual ? `Fase Atual: ${fase.nome}` : fase.nome}
      </Typography>
      <Typography variant="body2">
        Entrada:
        {fase.entrada}
      </Typography>
      <Typography variant="body2">
        Trabalhado:
        {fase.trabalhado}
      </Typography>
      <Typography variant="body2">
        Saída:
        {fase.saida}
      </Typography>
      <Typography variant="body2">
        Negociador:
        {fase.negociador}
      </Typography>
    </Paper>
  );
}

export default function AbaFases({ fasesTimeline }) {
  if (!fasesTimeline) {
    return (
      <CardWrapper>
        <Typography variant="body2">Carregando...</Typography>
      </CardWrapper>
    );
  }

  if (fasesTimeline.length === 0) {
    return (
      <CardWrapper>
        <Typography variant="body2">Nenhuma fase encontrada para o processo.</Typography>
      </CardWrapper>
    );
  }

  return (
    <CardWrapper>
      <Box sx={{ overflowX: 'auto' }}>
        <Box
          sx={{
            position: 'relative',
            minWidth: 760,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: 0,
              borderLeft: `2px dashed ${C.borderGrey}`,
              transform: 'translateX(-50%)',
            },
          }}
        >
          {fasesTimeline.map((fase, i) => {
            const aDireita = i % 2 === 0;
            return (
              <Box
                key={`${fase.nome}-${i}`}
                sx={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: aDireita ? 'row-reverse' : 'row',
                  alignItems: 'center',
                  gap: 2,
                  py: 1.5,
                }}
              >
                {/* Metade que recebe o card */}
                <Box sx={{
                  flex: 1, display: 'flex', justifyContent: aDireita ? 'flex-start' : 'flex-end', pl: aDireita ? 3 : 0, pr: aDireita ? 0 : 3,
                }}
                >
                  <CardFase fase={fase} />
                </Box>

                {/* Nó da timeline */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    backgroundColor: fase.atual ? C.tomato : C.navy,
                    border: `3px solid ${C.white}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: C.white,
                    zIndex: 1,
                    flexShrink: 0,
                    boxShadow: C.shadowCard,
                  }}
                >
                  {fase.atual ? null : <DirectionsWalkIcon sx={{ fontSize: 18 }} />}
                </Box>

                {/* Metade vazia */}
                <Box sx={{ flex: 1 }} />
              </Box>
            );
          })}
        </Box>
      </Box>
    </CardWrapper>
  );
}

AbaFases.propTypes = {
  fasesTimeline: PropTypes.arrayOf(PropTypes.object),
};

AbaFases.defaultProps = {
  fasesTimeline: null,
};
